import { Color } from '@persona-id/design-tokens';
import { BriefcaseIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { cloneDeep, isEqual, omit } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import Permissioned from 'dashboard/components/Permissioned';
import {
  DEFAULT_FIELD_SCHEMA,
  FieldSchemaEditorSupportedTypes,
  FieldSchemaEditorTable,
  FieldSchemaPanel,
  FocusReason,
  getAllFieldKeys,
  getAllFields,
  replaceNestedField,
  validateAllSchemas,
  validateSingleFieldSchema,
} from 'dashboard/components-lib/FieldSchemaEditor';
import { EditorableSchema } from 'dashboard/components-lib/FieldSchemaEditor/constants';
import FieldSchemaEditorV2 from 'dashboard/components-lib/FieldSchemaEditorForm/FieldSchemaEditorV2';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import { RIGHT_COLUMN_PORTAL_ID } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/CaseTemplateSettings';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Paragraph, Span } from 'design-system/components/Text';
import { FieldSchema } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

const MAX_RECOMMENDED_NESTING_DEPTH = 8;

const FieldSchemaPanelContainer = styled.div`
  width: 400px;
  position: absolute;
  top: 0px;
  right: 0px;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
`;

export interface FormCaseTemplateSettingsFieldsProps {
  initialFieldSchema: FieldSchema[];
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
}

const FormCaseTemplateSettingsFields = ({
  initialFieldSchema,
  hasCasesBasic,
  hasCasesEnhanced,
  hasCasesEnterprise,
}: FormCaseTemplateSettingsFieldsProps) => {
  const { control } = useFormContext();
  const hasProductFeatureEnabled = hasCasesBasic || hasCasesEnhanced || hasCasesEnterprise;
  const hasWritePermission = usePermission(Permission.CaseTemplateWrite);
  const editable = hasProductFeatureEnabled && hasWritePermission;
  const enableFieldSchemaEditorForm = useFeatureFlag('dashboard:enable-field-schema-editor-form');

  return enableFieldSchemaEditorForm ? (
    <FieldSchemaEditorV2
      canDeactivate={(fieldSchema, isNested) => {
        // Do not allow deactivate on nested schema
        return !isNested && !!fieldSchema.persisted;
      }}
      canDelete={(fieldSchema, _) => {
        return !fieldSchema.persisted;
      }}
      canEdit={(fieldSchema) => !fieldSchema.persisted}
      fieldNamePrefix='field-schema'
      nullState={<Span>This case template doesn&apos;t have any fields configured.</Span>}
      rightPanelPortalId={RIGHT_COLUMN_PORTAL_ID}
      editable={editable}
    />
  ) : (
    <>
      <Card>
        <Card.Body>
          <Controller
            control={control}
            name='field-schema'
            render={({ field: { onChange, value } }) => (
              <CaseTemplateFieldSchemaEditorTable
                inputSchema={value}
                initialFieldSchema={initialFieldSchema}
                editable={editable}
                onChange={onChange}
              />
            )}
          />
        </Card.Body>
      </Card>
    </>
  );
};

interface FieldSchemaEditorTableProps {
  initialFieldSchema: FieldSchemaEditorSupportedTypes[];
  inputSchema: FieldSchema[] | EditorableSchema[];
  editable: boolean;
  onChange: (value: FieldSchemaEditorSupportedTypes[]) => void;
}

const CaseTemplateFieldSchemaEditorTable = ({
  initialFieldSchema,
  inputSchema,
  editable,
  onChange,
}: FieldSchemaEditorTableProps) => {
  const [selectedFieldSchemaPath, setSelectedFieldSchemaPath] = useState<string>('');
  const [selectedFieldSchemaReason, setSelectedFieldSchemaReason] = useState<FocusReason>('add');

  // Things users are actively typing into. Could be invalid
  const [draftFieldSchemas, setDraftFieldSchemas] =
    useState<FieldSchemaEditorSupportedTypes[]>(initialFieldSchema);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [erroredKeys, setErroredKeys] = useState<string[]>([]);

  const refreshFieldEditorValue = useCallback((newSchemas: FieldSchemaEditorSupportedTypes[]) => {
    setDraftFieldSchemas(cloneDeep(newSchemas));
    setErroredKeys([]);
    onChange(newSchemas);
  }, []);

  useEffect(() => {
    setDraftFieldSchemas(cloneDeep(initialFieldSchema));
    setErroredKeys([]);
  }, [initialFieldSchema]);

  const selectedFieldSchema = useMemo(() => {
    if (!selectedFieldSchemaPath) {
      return null;
    }
    return getAllFields(draftFieldSchemas).find(({ key }) => key === selectedFieldSchemaPath)
      ?.fieldSchema;
  }, [draftFieldSchemas, selectedFieldSchemaPath]);

  const preexistingFieldKeys = useMemo(
    () => getAllFieldKeys(initialFieldSchema),
    [initialFieldSchema],
  );

  const fieldSchemasAreEqual = useMemo(() => {
    // We are making the assumption that currentKeys is a strict superset of preexistingActiveKeys
    const currentKeys = getAllFieldKeys(inputSchema);

    const preexistingActiveKeys = getAllFieldKeys(initialFieldSchema);
    return currentKeys.length === preexistingActiveKeys.length;
  }, [inputSchema, initialFieldSchema]);

  const hasUnsavedDrafts = useMemo(() => {
    // Omitting these attributes ensures that the legacy fields editor is compatible with the
    // decorated EditorableSchema that is used for the v2 field editor
    return !isEqual((omit(['persisted', 'identifier'], inputSchema), draftFieldSchemas));
  }, [inputSchema, draftFieldSchemas]);

  const handleUpdateSchema = (
    fieldSchemas: FieldSchemaEditorSupportedTypes[],
    autoValidate?: boolean,
  ) => {
    setDraftFieldSchemas(fieldSchemas);
    if (autoValidate) {
      const erroredKeys = validateAllSchemas(fieldSchemas);
      if (erroredKeys.length === 0) {
        refreshFieldEditorValue(fieldSchemas);
      } else {
        setErroredKeys(erroredKeys);
        setSelectedFieldSchemaPath(erroredKeys[0]);
      }
    }
  };

  const handleFocusSchema = (path: string, focusReason: FocusReason) => {
    if (!hasUnsavedDrafts) {
      setSelectedFieldSchemaPath(path);
      setSelectedFieldSchemaReason(focusReason);
    }
  };

  const tableIsEditable = useMemo(() => {
    return editable && !hasUnsavedDrafts;
  }, [editable, hasUnsavedDrafts]);

  const portalElement = document.getElementById(RIGHT_COLUMN_PORTAL_ID);

  return (
    <>
      <NavConfirmPrompt prevent={!fieldSchemasAreEqual} />
      <Card.Title
        actions={
          <Permissioned permission={Permission.CaseTemplateWrite}>
            <Button
              aria-label='Add new field'
              id='case-template-show__create-field-button'
              variant='basic'
              size='md'
              disabled={!tableIsEditable}
              prefixIcon={<PlusIcon />}
              onClick={() => {
                setDraftFieldSchemas([...draftFieldSchemas, DEFAULT_FIELD_SCHEMA]);
                setSelectedFieldSchemaPath(`${draftFieldSchemas.length}`);
              }}
            >
              Add Field
            </Button>
          </Permissioned>
        }
      >
        Fields
      </Card.Title>
      <Paragraph>Configure fields to store supplemental data on your cases.</Paragraph>
      <FieldSchemaEditorTable
        productIcon={<BriefcaseIcon size={48} color={Color.BorderIconSecondary} />}
        inputSchema={draftFieldSchemas}
        editable={tableIsEditable}
        focusedKey={selectedFieldSchemaPath}
        erroredKeys={erroredKeys}
        uneditableKeys={preexistingFieldKeys}
        nullState={<Span>This case template doesn&apos;t have any fields configured.</Span>}
        onUpdateSchema={handleUpdateSchema}
        onFocusSchema={handleFocusSchema}
      />
      {selectedFieldSchema != null &&
        portalElement &&
        createPortal(
          <FieldSchemaPanelContainer>
            <FieldSchemaPanel
              isComputedFieldsEnabled={false}
              fieldSchema={selectedFieldSchema || DEFAULT_FIELD_SCHEMA}
              isNewField={!preexistingFieldKeys.includes(selectedFieldSchemaPath)}
              errorMessage={errorMessage}
              selectReason={selectedFieldSchemaReason}
              allowHash={selectedFieldSchemaPath.split('.').length < MAX_RECOMMENDED_NESTING_DEPTH}
              onEditing={(fieldSchema) => {
                const { newSchemas, newPath } = replaceNestedField(
                  draftFieldSchemas,
                  selectedFieldSchemaPath,
                  fieldSchema,
                );
                const { message, validForDraft } = validateSingleFieldSchema(
                  fieldSchema,
                  newSchemas,
                );
                setErrorMessage(message);
                if (validForDraft) {
                  setDraftFieldSchemas(newSchemas);
                  setSelectedFieldSchemaPath(newPath);
                }
              }}
              onCancel={() => {
                setDraftFieldSchemas(cloneDeep(inputSchema));
                setSelectedFieldSchemaPath('');
                setErrorMessage('');
                setErroredKeys([]);
              }}
              onUpdate={(fieldSchema) => {
                const { newSchemas } = replaceNestedField(
                  draftFieldSchemas,
                  selectedFieldSchemaPath,
                  fieldSchema,
                );
                const { message } = validateSingleFieldSchema(fieldSchema, newSchemas);
                setErrorMessage(message);
                if (!message) {
                  refreshFieldEditorValue(newSchemas);
                  setSelectedFieldSchemaPath('');
                }
              }}
            />
          </FieldSchemaPanelContainer>,
          portalElement,
        )}
    </>
  );
};

export default FormCaseTemplateSettingsFields;
